import React, { useMemo } from 'react';
import Button from './Button';
import StatsPlot from './StatsPlot';

/**
 * Run color values
 */
const RUN_COLORS = {
  green: { label: 'Green (Beginner)', color: '#48bb78', bgColor: 'rgba(72, 187, 120, 0.15)' },
  blue: { label: 'Blue (Intermediate)', color: '#4299e1', bgColor: 'rgba(66, 153, 225, 0.15)' },
  red: { label: 'Red (Advanced)', color: '#f56565', bgColor: 'rgba(245, 101, 101, 0.15)' },
  black: { label: 'Black (Expert)', color: '#1a1a1a', bgColor: 'rgba(26, 26, 26, 0.3)' },
  unknown: { label: 'Unknown', color: '#888888', bgColor: 'rgba(136, 136, 136, 0.15)' }
};

/**
 * Component to display virtual agent details with stats during simulation
 */
const AgentDetailsPanel = ({ agentState, simulationCurrentTime, getUserByUid, getAgentStats, detectedFeature, onClose }) => {
  // Extract stats from currentDatasetLocations
  const statsHistory = useMemo(() => {
    if (!agentState.currentDatasetLocations || agentState.currentDatasetLocations.length === 0) {
      return [];
    }

    // Extract all unique stats entries
    const allStats = [];
    const seenStats = new Set();
    
    agentState.currentDatasetLocations.forEach(loc => {
      if (loc.stats && !seenStats.has(loc.stats.timestamp)) {
        seenStats.add(loc.stats.timestamp);
        allStats.push({
          timestamp: loc.stats.timestamp,
          maxVelocitymps: loc.stats.maxVelocitymps,
          totVerticalMeters: loc.stats.totVerticalMeters
        });
      }
    });
    
    // Sort by timestamp
    allStats.sort((a, b) => a.timestamp - b.timestamp);
    
    // Get the current behavior to find dataset start time
    const behaviors = agentState.agent.behaviors || [];
    const currentIdx = agentState.currentBehaviorIndex;
    if (currentIdx < behaviors.length) {
      const behavior = behaviors[currentIdx];
      if (behavior.type === 'datasetview' && behavior.start_time) {
        // Initialize with 0 values at the start time
        return [
          { timestamp: behavior.start_time, maxVelocitymps: 0, totVerticalMeters: 0 },
          ...allStats
        ];
      }
    }
    
    // If no start time, use first location timestamp
    if (allStats.length > 0) {
      return [
        { timestamp: allStats[0].timestamp, maxVelocitymps: 0, totVerticalMeters: 0 },
        ...allStats
      ];
    }
    
    return [];
  }, [agentState.currentDatasetLocations, agentState.agent, agentState.currentBehaviorIndex]);

  // Get dataset timestamp for current time
  const getDatasetTimestamp = () => {
    const behaviors = agentState.agent.behaviors || [];
    const currentIdx = agentState.currentBehaviorIndex;
    if (currentIdx < behaviors.length) {
      const behavior = behaviors[currentIdx];
      if (behavior.type === 'datasetview' && behavior.start_time) {
        return behavior.start_time + simulationCurrentTime;
      }
    }
    return null;
  };

  const datasetTimestamp = getDatasetTimestamp();

  return (
    <div className="agent-details-panel">
      <div className="agent-details-header">
        <h2>{getUserByUid(agentState.agent.uid)?.name || agentState.agent.uid}</h2>
        <Button
          variant="outline"
          size="small"
          onClick={onClose}
        >
          Close
        </Button>
      </div>
      <div className="agent-details-content">
        {/* Stats Section - only show if we have stats */}
        {statsHistory.length > 1 && datasetTimestamp && (
          <div className="telemetry-section stats-section">
            <h3>Statistics</h3>
            <StatsPlot
              data={statsHistory}
              currentTime={datasetTimestamp}
              dataKey="maxVelocitymps"
              label="Max Velocity"
              unit="km/h"
              color="#667eea"
            />
            <StatsPlot
              data={statsHistory}
              currentTime={datasetTimestamp}
              dataKey="totVerticalMeters"
              label="Total Vertical"
              unit="m"
              color="#764ba2"
            />
          </div>
        )}

        <div className="telemetry-section">
          <h3>Position</h3>
          <div className="telemetry-grid">
            <div className="telemetry-item">
              <span className="telemetry-label">Location</span>
              <span className="telemetry-value">
                {agentState.currentPosition
                  ? `${agentState.currentPosition.lat.toFixed(6)}°, ${agentState.currentPosition.lon.toFixed(6)}°`
                  : 'No location'}
              </span>
            </div>
          </div>
        </div>

        {/* Detected Feature Section */}
        {detectedFeature && (
          <div className="telemetry-section detected-feature-section">
            <h3>Detected Feature</h3>
            <div 
              className="detected-feature-card"
              style={{
                backgroundColor: detectedFeature.type === 'run' 
                  ? (RUN_COLORS[detectedFeature.color] || RUN_COLORS.unknown).bgColor
                  : 'rgba(102, 126, 234, 0.15)',
                borderColor: detectedFeature.type === 'run'
                  ? (RUN_COLORS[detectedFeature.color] || RUN_COLORS.unknown).color
                  : '#667eea'
              }}
            >
              <div className="detected-feature-type">
                {detectedFeature.type === 'run' ? '⛷️ Run' : '🚡 Lift'}
              </div>
              <div className="detected-feature-name">
                {detectedFeature.name || detectedFeature.id}
              </div>
              {detectedFeature.type === 'run' && detectedFeature.color && (
                <div 
                  className="detected-feature-color"
                  style={{ color: (RUN_COLORS[detectedFeature.color] || RUN_COLORS.unknown).color }}
                >
                  {(RUN_COLORS[detectedFeature.color] || RUN_COLORS.unknown).label}
                </div>
              )}
              <div className="detected-feature-id">
                ID: {detectedFeature.id}
              </div>
            </div>
          </div>
        )}

        <div className="telemetry-section">
          <h3>Simulation Status</h3>
          <div className="telemetry-grid">
            <div className="telemetry-item">
              <span className="telemetry-label">Current Behavior</span>
              <span className="telemetry-value">
                {(() => {
                  const behaviors = agentState.agent.behaviors || [];
                  const currentIdx = agentState.currentBehaviorIndex;
                  if (currentIdx >= behaviors.length) {
                    return 'Completed';
                  }
                  const behavior = behaviors[currentIdx];
                  if (behavior.type === 'wait') {
                    return `Wait (${behavior.duration}s)`;
                  } else if (behavior.type === 'datasetview') {
                    return `Dataset View`;
                  }
                  return 'Unknown';
                })()}
              </span>
            </div>
            <div className="telemetry-item">
              <span className="telemetry-label">Behaviors Completed</span>
              <span className="telemetry-value">
                {agentState.currentBehaviorIndex} / {agentState.agent.behaviors?.length || 0}
              </span>
            </div>
            <div className="telemetry-item">
              <span className="telemetry-label">Status</span>
              <span className="telemetry-value">
                {agentState.completed ? 'Completed' : 'Active'}
              </span>
            </div>
          </div>
        </div>
        <div className="telemetry-section">
          <h3>Agent Info</h3>
          <div className="telemetry-grid">
            <div className="telemetry-item">
              <span className="telemetry-label">Total Behaviors</span>
              <span className="telemetry-value">
                {getAgentStats(agentState.agent).behaviorCount}
              </span>
            </div>
            <div className="telemetry-item">
              <span className="telemetry-label">Total Duration</span>
              <span className="telemetry-value">
                {getAgentStats(agentState.agent).totalMinutes} minutes
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentDetailsPanel;
